import { Router } from '@angular/router';
import { Product, ProductsList } from '../interfaces/products.interface';
import { Configurator, ConfiguratorsList } from '../interfaces/configurators.interface';
import { Photo } from '../interfaces/photo.interface';
import { Price } from '../interfaces/price.interface';
import { Crumbs } from '../interfaces/crumb.interface';

export function getConfiguratorUrl(
    configurator: Configurator | number,
    productId: number = null,
    categoryId: number = null,
    salesDocumentId: string = null
): string {
    const configuratorObj = configurator as Configurator;
    if (configuratorObj && typeof configuratorObj === 'object') {
        if (configuratorObj?.isClassicConfigurator && configuratorObj?.classicConfiguratorType) {
            const params = configuratorObj?.classicConfiguratorType;
            return `/configurator/configure/${params}`;
        } else {
            const params =
                configuratorObj?.id +
                (productId ? '/' + productId : salesDocumentId ? '/0' : '') +
                (categoryId ? '/' + categoryId : salesDocumentId ? '/0' : '') +
                (salesDocumentId ? '/' + salesDocumentId : '');
            return `/configurator/configure/complex/${params}`;
        }
    } else {
        const params =
            configurator +
            (productId ? '/' + productId : salesDocumentId ? '/0' : '') +
            (categoryId ? '/' + categoryId : salesDocumentId ? '/0' : '') +
            (salesDocumentId ? '/' + salesDocumentId : '');
        return `/configurator/configure/complex/${params}`;
    }
}

export function getProductCardUrl(categoryId: number, productId: number): string {
    return `/trade/category/${categoryId}/product-card/${productId}`;
}

export function getSimpleProductCardUrl(productId: number): string {
    return `/trade/product-card/${productId}`;
}

export function getCategoryUrl(categoryId: number): string {
    return `/trade/category/${categoryId}`;
}

export function getTradeUrl(hasDashboard = false): string {
    return hasDashboard ? `/trade/products` : `/trade/`;
}

export function getSearchUrl(search: string): string {
    return `/trade/search/${search.replace(/\//g, '%2F')}`;
}

export function replaceMainPhoto(mainPhoto: Photo | undefined, photos: Photo[]): Photo {
    if (!mainPhoto || !mainPhoto?.img) {
        if (photos && photos[0]?.img) {
            mainPhoto = photos[0];
        } else {
            mainPhoto = {
                img: 'assets/no-photo.jpg',
                title: '',
                alt: '',
            };
        }
    } else {
        const mainPhotoName = mainPhoto.img;
        mainPhoto.img = '/files/photos/' + mainPhoto.img;

        mainPhoto.variants = {
            webp: '/files/photos/webp/' + mainPhotoName + '.webp',
            listSmX1: {
                webp: '/files/photos/list-sm-x1/webp/' + mainPhotoName + '.webp',
                png: '/files/photos/list-sm-x1/' + mainPhotoName + '.png',
            },
            listSmX2: {
                webp: '/files/photos/list-sm-x2/webp/' + mainPhotoName + '.webp',
                png: '/files/photos/list-sm-x2/' + mainPhotoName + '.png',
            },
            listMdX1: {
                webp: '/files/photos/list-md-x1/webp/' + mainPhotoName + '.webp',
                png: '/files/photos/list-md-x1/' + mainPhotoName + '.png',
            },
            listMdX2: {
                webp: '/files/photos/list-md-x2/webp/' + mainPhotoName + '.webp',
                png: '/files/photos/list-md-x2/' + mainPhotoName + '.png',
            },
        };
    }
    return mainPhoto;
}

export function replacePhotos(photos: Photo[]): Photo[] {
    for (const photo of photos) {
        if (photo?.img) {
            const photoImgName = photo.img;
            photo.img = '/files/photos/' + photo.img;

            photo.variants = {
                webp: '/files/photos/webp/' + photoImgName + '.webp',
                listSmX1: {
                    webp: '/files/photos/list-sm-x1/webp/' + photoImgName + '.webp',
                    png: '/files/photos/list-sm-x1/' + photoImgName + '.png',
                },
                listSmX2: {
                    webp: '/files/photos/list-sm-x2/webp/' + photoImgName + '.webp',
                    png: '/files/photos/list-sm-x2/' + photoImgName + '.png',
                },
                listMdX1: {
                    webp: '/files/photos/list-md-x1/webp/' + photoImgName + '.webp',
                    png: '/files/photos/list-md-x1/' + photoImgName + '.png',
                },
                listMdX2: {
                    webp: '/files/photos/list-md-x2/webp/' + photoImgName + '.webp',
                    png: '/files/photos/list-md-x2/' + photoImgName + '.png',
                },
            };
        } else {
            photo.img = 'assets/no-photo.jpg';
        }
    }
    return photos;
}

export function handleMainPhotosInProducts(products: ProductsList): ProductsList {
    for (const product of products) {
        product.mainPhoto = replaceMainPhoto(product.mainPhoto, product.photos);
    }
    return products;
}
export function handlePricesInProducts(
    products: ProductsList,
    prices: Record<number, Price>
): ProductsList {
    if (products && prices) {
        for (const product of products) {
            product.price = prices[product.id];
            if (!product.price) {
                product.price = {
                    value: null,
                    isGross: null,
                    taxRate: null,
                    resetTaxRate: null,
                    currency: null,
                    priceUnits: [],
                    revision: 0,
                };
            }
        }
    }
    return products;
}
export function handlePhotosInProducts(products: ProductsList): ProductsList {
    for (const product of products) {
        product.photos = replacePhotos(product.photos);
    }
    return products;
}

export function handleMainPhotosInConfigurators(
    configurators: ConfiguratorsList
): ConfiguratorsList {
    for (const configurator of configurators) {
        configurator.mainPhoto = replaceMainPhoto(configurator.mainPhoto, configurator.photos);
    }
    return configurators;
}

export function handlePhotosInConfigurators(configurators: ConfiguratorsList): ConfiguratorsList {
    for (const configurator of configurators) {
        configurator.photos = replacePhotos(configurator.photos);
    }
    return configurators;
}

export const isStatic = (type: string) => type === 'static';
export const isConfigured = (product: Product) =>
    !isStatic(product.type) && product.isConfigured && product.complexConfiguratorId;

export const navigate = (router: Router, uri: string) => {
    router.navigateByUrl('/', { skipLocationChange: true }).then(() => router.navigate([uri]));
};

export const scrollTop = (top = 0, left = 0) => {
    window.scroll({
        top,
        left,
        behavior: 'smooth',
    });
};
